import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import {
	editDishRestaurantPropertiesInitialState,
	EditDishRestaurantPropertiesContext,
	TTypeOfModalWindow,
} from './editDishRestaurantPropertiesContext';
import { IAppContext, AppContext } from 'appState/appContext';
import { editDishRestaurantPropertiesReducer } from './editDishRestaurantPropertiesReducer';
import { EditDishRestaurantPropertiesActionTypes } from './editDishRestaurantPropertiesActions';
import {
	UserRoles,
	IGetDishInfoV2,
	IUpdateDishRestaurantProperties,
	IGetDishRestaurantProperties,
	IGetDishRestaurantPropertiesArguments,
	IRestaurantPropertiesModifierGroup,
	TPortionSelectionMode,
} from 'types';
import { defaultPortionSelectionMode } from 'appConstants/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { getDishInfoV2, getDishRestaurantProperties } from 'backend/dishes';
import { useFetchData } from 'hooks/useFetchData';

export const EditDishRestaurantPropertiesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editDishRestaurantPropertiesReducer, editDishRestaurantPropertiesInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response: dishInfoResponse, loading: dishInfoLoading } = useFetchData<IGetDishInfoV2, string>(
		dishID ? getDishInfoV2 : null,
		dishID || '',
	);

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetDishRestaurantProperties, IGetDishRestaurantPropertiesArguments>(
		state.IDSelectedRestaurant ? getDishRestaurantProperties : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			dishId: state.dishId,
		},
	);

	useEffect(() => {
		dishInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [dishInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (dishInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const fetchedRestaurants = hasAccess
				? dishInfoResponse.data.restaurants
				: dishInfoResponse.data.restaurants.filter((restaurant) => userRestaurantIDs.includes(restaurant.id));

			const restaurantsWithPortionsIds = dishInfoResponse.data.restaurantsWithPortions.map(
				(restaurant) => restaurant.id,
			);

			dispatch({
				type: EditDishRestaurantPropertiesActionTypes.SET_ALL_DATA,
				payload: {
					dishId: dishInfoResponse.data.id,
					dishName: dishInfoResponse.data.name || '',
					isCreatedOnPos: dishInfoResponse.data.isCreatedOnPos,
					isPortionEnabled: dishInfoResponse.data.isPortionEnabled,
					customerName: dishInfoResponse.data.customer.name || '',
					hasSchedulePriority: dishInfoResponse.data.overrideScheduleAvailability,
					activationSchedule: dishInfoResponse.data.activationCron || '',
					deactivationSchedule: dishInfoResponse.data.deactivationCron || '',
					fetchedRestaurants,
					restaurantsWithPortionsIds,
				},
			});
		}
	}, [dishID, loggedUser.restaurants, hasAccess, dishInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: EditDishRestaurantPropertiesActionTypes.SET_DATA_FROM_RESTAURANT,
				payload: {
					categories: restaurantPropertiesResponse.data.categories,
					price: restaurantPropertiesResponse.data.price,
					openPrice: restaurantPropertiesResponse.data.openPrice,
					openPriceMaxThreshold: restaurantPropertiesResponse.data.openPriceMaxThreshold,
					hasOpenPrice: restaurantPropertiesResponse.data.hasOpenPrice,
					inStop: restaurantPropertiesResponse.data.isDisabled,
					isCommentVisible: restaurantPropertiesResponse.data.isCommentVisible,
					isFiscalized: restaurantPropertiesResponse.data.isFiscalized,
					taxPercent: restaurantPropertiesResponse.data.taxPercent,
					isTaxPercentProtectedFromSync: restaurantPropertiesResponse.data.isTaxPercentProtectedFromSync,
					activationSchedule: restaurantPropertiesResponse.data.activationCron || '',
					deactivationSchedule: restaurantPropertiesResponse.data.deactivationCron || '',
					IDsSelectedModifierGroups: restaurantPropertiesResponse.data.modifierGroups.map(
						(modifierGroup) => modifierGroup.id,
					),
					modifierGroupsWithAdditionalData: restaurantPropertiesResponse.data.modifierGroups,
					hasPortions: restaurantPropertiesResponse.data.hasPortions,
					portionSelectionMode: restaurantPropertiesResponse.data.portionSelectionMode,
					hasPortionBasedModGroups: restaurantPropertiesResponse.data.hasPortionBasedModGroups,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: EditDishRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.IDSelectedRestaurant]);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: EditDishRestaurantPropertiesActionTypes.SET_ID_RESTAURANT,
				payload: {
					IDSelectedRestaurant: value,
					categories: value ? state.categories : [],
					price: value ? state.price : null,
					openPrice: value ? state.openPrice : null,
					openPriceMaxThreshold: value ? state.openPriceMaxThreshold : null,
					hasOpenPrice: value ? state.hasOpenPrice : false,
					inStop: value ? state.inStop : false,
					isCommentVisible: value ? state.isCommentVisible : false,
					isFiscalized: value ? state.isFiscalized : false,
					taxPercent: value ? state.taxPercent : null,
					isTaxPercentProtectedFromSync: value ? state.isTaxPercentProtectedFromSync : false,
					activationSchedule: value ? state.activationSchedule : '',
					deactivationSchedule: value ? state.deactivationSchedule : '',
					IDsSelectedModifierGroups: value ? state.IDsSelectedModifierGroups : [],
					modifierGroupsWithAdditionalData: value ? state.modifierGroupsWithAdditionalData : [],
					hasPortions: value ? state.hasPortions : false,
					portionSelectionMode: value ? state.portionSelectionMode : defaultPortionSelectionMode,
					hasPortionBasedModGroups: value ? state.hasPortionBasedModGroups : false,
				},
			});
		},
		[
			state.categories,
			state.price,
			state.openPrice,
			state.openPriceMaxThreshold,
			state.hasOpenPrice,
			state.inStop,
			state.isCommentVisible,
			state.isFiscalized,
			state.taxPercent,
			state.isTaxPercentProtectedFromSync,
			state.activationSchedule,
			state.deactivationSchedule,
			state.IDsSelectedModifierGroups,
			state.modifierGroupsWithAdditionalData,
			state.portionSelectionMode,
		],
	);

	const changePrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const changeOpenPrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_OPEN_PRICE,
			payload: {
				openPrice: value,
			},
		});
	}, []);

	const changeOpenPriceMaxThreshold = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_OPEN_PRICE_MAX_THRESHOLD,
			payload: {
				openPriceMaxThreshold: value,
			},
		});
	}, []);

	const toggleHasOpenPrice = useCallback(() => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.TOGGLE_HAS_OPEN_PRICE,
		});
	}, []);

	const toggleInStop = useCallback(() => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.TOGGLE_IN_STOP,
		});
	}, []);

	const toggleIsCommentVisible = useCallback(() => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.TOGGLE_IS_COMMENT_VISIBLE,
		});
	}, []);

	const toggleIsFiscalized = useCallback(() => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.TOGGLE_IS_FISCALIZED,
		});
	}, []);

	const changePortionSelectionMode = useCallback((value: TPortionSelectionMode) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_PORTION_SELECTION_MODE,
			payload: {
				portionSelectionMode: value,
			},
		});
	}, []);

	const changeTaxPercent = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_TAX_PERCENT,
			payload: {
				taxPercent: value,
			},
		});
	}, []);

	const toggleIsTaxPercentProtectedFromSync = useCallback(() => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.TOGGLE_IS_PROTECT_FROM_SYNC,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setModifierGroups = useCallback((value: string[]) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.SET_MODIFIER_GROUP,
			payload: {
				IDsSelectedModifierGroups: value,
			},
		});
	}, []);

	const setModifierGroupsWithAdditionalData = useCallback((value: IRestaurantPropertiesModifierGroup[]) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.SET_MODIFIER_GROUP_WITH_ADDITIONAL_DATA,
			payload: {
				modifierGroupsWithAdditionalData: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateDishRestaurantProperties>) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditDishRestaurantPropertiesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditDishRestaurantPropertiesContext.Provider
			value={{
				refetchRestPropsData,
				setIDRestaurant,
				changePrice,
				changeOpenPrice,
				changeOpenPriceMaxThreshold,
				toggleHasOpenPrice,
				toggleInStop,
				toggleIsCommentVisible,
				toggleIsFiscalized,
				changePortionSelectionMode,
				changeTaxPercent,
				toggleIsTaxPercentProtectedFromSync,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setModifierGroups,
				setModifierGroupsWithAdditionalData,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				customerName: state.customerName,
				fetchedRestaurants: state.fetchedRestaurants,
				restaurantsWithPortionsIds: state.restaurantsWithPortionsIds,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				dishId: state.dishId,
				dishName: state.dishName,
				isCreatedOnPos: state.isCreatedOnPos,
				isPortionEnabled: state.isPortionEnabled,
				categories: state.categories,
				hasSchedulePriority: state.hasSchedulePriority,
				price: state.price,
				openPrice: state.openPrice,
				openPriceMaxThreshold: state.openPriceMaxThreshold,
				hasOpenPrice: state.hasOpenPrice,
				inStop: state.inStop,
				isCommentVisible: state.isCommentVisible,
				isFiscalized: state.isFiscalized,
				taxPercent: state.taxPercent,
				isTaxPercentProtectedFromSync: state.isTaxPercentProtectedFromSync,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				IDsSelectedModifierGroups: state.IDsSelectedModifierGroups,
				modifierGroupsWithAdditionalData: state.modifierGroupsWithAdditionalData,
				hasPortions: state.hasPortions,
				portionSelectionMode: state.portionSelectionMode,
				hasPortionBasedModGroups: state.hasPortionBasedModGroups,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditDishRestaurantPropertiesContext.Provider>
	);
};
